import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Lightning, Trophy, X, Plus, ArrowRight, Target, ChartBar } from "@phosphor-icons/react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import SEO from "@/components/SEO";
import PublicNav from "@/components/PublicNav";

const API = process.env.REACT_APP_BACKEND_URL;
const PC = { QB: "#ff6b6b", RB: "#4ecdc4", WR: "#45b7d1", TE: "#f9ca24" };
const POSITIONS = ["QB", "RB", "WR", "TE"];

function ArchetypeCard({ arch, isMatch, isCompact }) {
  const icons = { "QB-Heavy": "🎯", "RB-Anchor": "🏃", "WR-Factory": "📡", "Elite-TE": "🏈", "Balanced": "⚖️" };
  return (
    <div
      className={`border p-4 rounded-sm transition-all ${isMatch ? "border-[#ccff00] bg-[#ccff00]/5" : "border-[#262626] bg-[#171717]"} ${isCompact ? "" : ""}`}
      data-testid={`archetype-${arch.label.toLowerCase()}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <span className="text-lg mr-1.5">{icons[arch.label] || "📊"}</span>
          <span className="text-sm font-bold text-white">{arch.title}</span>
          {isMatch && <span className="ml-2 text-[10px] font-bold text-[#ccff00] bg-[#ccff00]/10 px-2 py-0.5 rounded-sm uppercase">Your Match</span>}
        </div>
        <span className="text-xs font-mono text-[#888]">{arch.pct}%</span>
      </div>
      <p className="text-xs text-[#888] mb-3">{arch.description}</p>
      {!isCompact && (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {POSITIONS.map(pos => (
              <div key={pos} className="text-center">
                <div className="text-sm font-bold font-mono" style={{ color: PC[pos] }}>{arch.avg_early?.[pos] ?? "-"}</div>
                <div className="text-[10px] text-[#555]">{pos} early</div>
              </div>
            ))}
          </div>
          <div className="border-t border-[#262626] pt-2 mt-2">
            <p className="text-[10px] text-[#ccff00] font-bold uppercase mb-1">Strategy</p>
            <p className="text-[10px] text-[#888]">{arch.strategy}</p>
            <p className="text-[10px] text-[#555] mt-1">Best for: {arch.best_for}</p>
          </div>
        </>
      )}
    </div>
  );
}

function PickEntry({ pick, index, onRemove, onChange }) {
  return (
    <div className="flex items-center gap-2 p-2 border border-[#262626] bg-[#171717] rounded-sm">
      <span className="text-[10px] font-mono text-[#555] w-6">R{pick.round}</span>
      <select
        value={pick.position}
        onChange={e => onChange(index, "position", e.target.value)}
        className="bg-[#0a0a0a] border border-[#333] text-white text-xs px-2 py-1 rounded-sm flex-1"
        data-testid={`pick-position-${index}`}
      >
        <option value="">Pos</option>
        {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
      </select>
      <input
        type="number"
        min={1}
        max={40}
        value={pick.round}
        onChange={e => onChange(index, "round", parseInt(e.target.value) || 1)}
        className="bg-[#0a0a0a] border border-[#333] text-white text-xs px-2 py-1 rounded-sm w-14"
        placeholder="Rd"
        data-testid={`pick-round-${index}`}
      />
      <button onClick={() => onRemove(index)} className="text-[#555] hover:text-red-400 transition-colors" data-testid={`remove-pick-${index}`}>
        <X size={14} />
      </button>
    </div>
  );
}

function ResultPanel({ result, archetypes }) {
  if (!result) return null;
  const matchArch = archetypes?.find(a => a.label === result.archetype);

  const pieData = POSITIONS.map(pos => ({
    name: pos,
    value: result.total_by_pos?.[pos] || 0,
    color: PC[pos],
  })).filter(d => d.value > 0);

  return (
    <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-5 rounded-sm" data-testid="archetype-result">
      <div className="flex items-center gap-2 mb-3">
        <Target size={18} weight="fill" className="text-[#ccff00]" />
        <h3 className="text-lg font-bold text-white">{matchArch?.title || result.archetype}</h3>
      </div>

      {matchArch && (
        <p className="text-sm text-[#a3a3a3] mb-4">{matchArch.description}</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase text-[#555] mb-2">YOUR EARLY ROUND ALLOCATION</p>
          <div className="grid grid-cols-4 gap-2">
            {POSITIONS.map(pos => (
              <div key={pos} className="text-center p-2 border border-[#262626] bg-[#171717] rounded-sm">
                <div className="text-lg font-bold font-mono" style={{ color: PC[pos] }}>{result[`${pos.toLowerCase()}_early_pct`] ?? 0}%</div>
                <div className="text-[10px] text-[#555]">{pos}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center">
          <ResponsiveContainer width={140} height={140}>
            <PieChart>
              <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={55} innerRadius={30} paddingAngle={2}>
                {pieData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#171717", border: "1px solid #262626", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {matchArch && (
        <div className="border-t border-[#262626] pt-3 mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-[10px] font-bold uppercase text-[#ccff00] mb-1">CHAMPION COMPARISON</p>
            <p className="text-xs text-[#888]"><strong className="text-white">{matchArch.pct}%</strong> of champions used this archetype. {matchArch.count.toLocaleString()} out of {archetypes?.reduce((s, a) => s + a.count, 0)?.toLocaleString()} classified drafts.</p>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase text-[#ccff00] mb-1">STRATEGY TIP</p>
            <p className="text-xs text-[#888]">{matchArch.strategy}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function DraftArchetypeFinder() {
  const [archetypes, setArchetypes] = useState(null);
  const [picks, setPicks] = useState([
    { position: "QB", round: 1 }, { position: "WR", round: 2 },
    { position: "RB", round: 3 }, { position: "WR", round: 4 },
    { position: "QB", round: 5 }, { position: "WR", round: 6 },
    { position: "RB", round: 7 }, { position: "TE", round: 8 },
    { position: "WR", round: 9 }, { position: "RB", round: 10 },
    { position: "WR", round: 11 }, { position: "QB", round: 12 },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leagueId, setLeagueId] = useState("");
  const [leagueResult, setLeagueResult] = useState(null);
  const [leagueLoading, setLeagueLoading] = useState(false);
  const [leagueError, setLeagueError] = useState("");
  const [tab, setTab] = useState("manual");

  useEffect(() => {
    fetch(`${API}/api/drafts/archetypes`)
      .then(r => r.ok ? r.json() : null)
      .then(setArchetypes)
      .catch(() => {});
  }, []);

  const classifyPicks = useCallback(async () => {
    const valid = picks.filter(p => p.position && p.round > 0);
    if (valid.length < 10) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/drafts/classify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ picks: valid }),
      });
      if (r.ok) setResult(await r.json());
    } catch {}
    setLoading(false);
  }, [picks]);

  const classifyLeague = useCallback(async () => {
    if (!leagueId.trim()) return;
    setLeagueLoading(true);
    setLeagueError("");
    setLeagueResult(null);
    try {
      const r = await fetch(`${API}/api/drafts/classify-league/${leagueId.trim()}`);
      if (r.ok) {
        setLeagueResult(await r.json());
      } else {
        const err = await r.json().catch(() => ({}));
        setLeagueError(err.detail || "Could not find a completed startup draft for this league.");
      }
    } catch {
      setLeagueError("Network error. Check the league ID and try again.");
    }
    setLeagueLoading(false);
  }, [leagueId]);

  const addPick = () => setPicks(prev => [...prev, { position: "", round: prev.length + 1 }]);
  const removePick = (i) => setPicks(prev => prev.filter((_, idx) => idx !== i));
  const updatePick = (i, field, val) => setPicks(prev => prev.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  const totalClassified = archetypes?.total_drafts_classified || 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="Draft Archetype Finder: Which Champion Build Matches Your Roster? — Dynasty Trade Maker"
        description="Find out which dynasty champion draft archetype your startup roster matches. Analyzed 1,100+ real startup drafts from championship-winning Sleeper leagues."
        path="/tools/draft-archetype"
      />
      <PublicNav />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/guide/dynasty-startup-draft" className="text-[10px] text-[#555] hover:text-[#a3a3a3] no-underline uppercase tracking-wider">Draft Guide</Link>
        <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#ccff00] block mt-4 mb-2">
          FROM {totalClassified.toLocaleString()} CLASSIFIED CHAMPION STARTUP DRAFTS
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2" data-testid="archetype-finder-title">
          DRAFT ARCHETYPE FINDER
        </h1>
        <p className="text-sm text-[#a3a3a3] mb-6 max-w-2xl">
          Every championship-winning dynasty roster started with a draft. We classified {totalClassified.toLocaleString()} real startup drafts from champion leagues into distinct archetypes. Enter your picks or paste a Sleeper league ID to see which champion build you match.
        </p>

        {/* Champion Archetypes Overview */}
        {archetypes && (
          <div className="mb-8">
            <h2 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-3 flex items-center gap-1.5">
              <Trophy size={14} weight="fill" className="text-[#ccff00]" /> CHAMPION DRAFT ARCHETYPES
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {archetypes.archetypes?.map(a => (
                <ArchetypeCard
                  key={a.label}
                  arch={a}
                  isMatch={result?.archetype === a.label || leagueResult?.archetype === a.label}
                  isCompact
                />
              ))}
            </div>
          </div>
        )}

        {/* Tab Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setTab("manual")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${tab === "manual" ? "bg-[#ccff00] text-black" : "bg-[#171717] text-[#888] border border-[#333] hover:border-[#555]"}`}
            data-testid="tab-manual"
          >
            Enter Picks
          </button>
          <button
            onClick={() => setTab("league")}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-all ${tab === "league" ? "bg-[#ccff00] text-black" : "bg-[#171717] text-[#888] border border-[#333] hover:border-[#555]"}`}
            data-testid="tab-league"
          >
            Sleeper League ID
          </button>
        </div>

        {/* Manual Entry */}
        {tab === "manual" && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-bold text-white uppercase">Your Draft Picks</h2>
              <span className="text-[10px] text-[#555]">{picks.length} picks entered</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
              {picks.map((p, i) => (
                <PickEntry key={i} pick={p} index={i} onRemove={removePick} onChange={updatePick} />
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={addPick} className="text-xs text-[#888] hover:text-white flex items-center gap-1 transition-colors" data-testid="add-pick-btn">
                <Plus size={12} /> Add pick
              </button>
              <button
                onClick={classifyPicks}
                disabled={loading || picks.filter(p => p.position).length < 10}
                className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] font-bold hover:bg-[#b8e600] transition-colors disabled:opacity-40 flex items-center gap-1.5"
                data-testid="classify-btn"
              >
                {loading ? "Analyzing..." : <><ChartBar size={12} weight="fill" /> Find My Archetype</>}
              </button>
            </div>
            {result && <div className="mt-6"><ResultPanel result={result} archetypes={archetypes?.archetypes} /></div>}
          </div>
        )}

        {/* League ID Entry */}
        {tab === "league" && (
          <div className="mb-6">
            <p className="text-xs text-[#888] mb-3">
              Paste your Sleeper league ID and we will auto-detect the startup draft and classify it. Find your league ID in the Sleeper app URL or league settings.
            </p>
            <div className="flex gap-2 mb-4">
              <input
                value={leagueId}
                onChange={e => setLeagueId(e.target.value)}
                placeholder="e.g. 1204877167427588096"
                className="flex-1 bg-[#171717] border border-[#333] text-white text-sm px-3 py-2 rounded-sm placeholder:text-[#555]"
                data-testid="league-id-input"
              />
              <button
                onClick={classifyLeague}
                disabled={leagueLoading || !leagueId.trim()}
                className="h-10 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] font-bold hover:bg-[#b8e600] transition-colors disabled:opacity-40"
                data-testid="classify-league-btn"
              >
                {leagueLoading ? "Analyzing..." : "Analyze Draft"}
              </button>
            </div>
            {leagueError && <p className="text-xs text-red-400 mb-4" data-testid="league-error">{leagueError}</p>}
            {leagueResult && (
              <div className="space-y-4">
                <div className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                  <p className="text-[10px] font-bold uppercase text-[#555] mb-2">DRAFT DETAILS</p>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div><div className="text-lg font-bold font-mono text-white">{leagueResult.rounds}</div><div className="text-[10px] text-[#555]">Rounds</div></div>
                    <div><div className="text-lg font-bold font-mono text-white">{leagueResult.total_picks}</div><div className="text-[10px] text-[#555]">Total Picks</div></div>
                    <div><div className="text-lg font-bold font-mono text-white capitalize">{leagueResult.draft_type}</div><div className="text-[10px] text-[#555]">Draft Type</div></div>
                  </div>
                </div>
                <ResultPanel result={leagueResult} archetypes={archetypes?.archetypes} />
                {leagueResult.picks_summary?.length > 0 && (
                  <div className="border border-[#262626] bg-[#171717] rounded-sm overflow-hidden">
                    <div className="p-3 border-b border-[#262626]">
                      <p className="text-[10px] font-bold uppercase text-[#555]">FIRST 48 PICKS</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-0">
                      {leagueResult.picks_summary.map((p, i) => (
                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b border-r border-[#1a1a1a] text-[11px]">
                          <span className="font-mono text-[#555] w-5">{p.pick_no}</span>
                          <span className="font-bold px-1 py-0.5 rounded-sm text-[10px]" style={{ color: PC[p.position] || "#888", backgroundColor: `${PC[p.position] || "#888"}15` }}>{p.position}</span>
                          <span className="text-[#a3a3a3] truncate">{p.player_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Deep Dive - Expanded Archetypes */}
        {archetypes && (
          <div className="mt-8">
            <h2 className="font-heading text-2xl text-white uppercase mb-4">THE FIVE CHAMPION ARCHETYPES</h2>
            <p className="text-sm text-[#a3a3a3] mb-6">
              Based on {totalClassified.toLocaleString()} classified startup drafts from championship-winning Sleeper leagues. Each archetype represents a distinct draft philosophy that produced champions.
            </p>
            <div className="space-y-4">
              {archetypes.archetypes?.map(a => (
                <ArchetypeCard
                  key={a.label}
                  arch={a}
                  isMatch={result?.archetype === a.label || leagueResult?.archetype === a.label}
                />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center my-8" data-testid="archetype-cta">
          <h3 className="font-heading text-lg text-white uppercase mb-2">BUILD LIKE A CHAMPION</h3>
          <p className="text-xs text-[#888] mb-4">Use our tools to optimize every pick and trade during your startup draft.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/trade-calculator" className="h-9 px-5 bg-[#ccff00] text-black rounded-sm text-[11px] inline-flex items-center gap-1.5 no-underline font-bold hover:bg-[#b8e600] transition-colors" data-testid="archetype-cta-calc">
              <Lightning size={12} weight="fill" /> TRADE CALCULATOR
            </Link>
            <Link to="/guide/dynasty-startup-draft" className="text-[11px] text-[#ccff00] no-underline hover:underline flex items-center gap-1">
              Full Draft Guide <ArrowRight size={11} />
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center text-[10px] text-[#444] space-x-3">
          <Link to="/guide/dynasty-startup-draft" className="hover:text-[#a3a3a3] no-underline">Draft Guide</Link>
          <Link to="/research/championship-report" className="hover:text-[#a3a3a3] no-underline">Championship Report</Link>
          <Link to="/rankings" className="hover:text-[#a3a3a3] no-underline">Rankings</Link>
        </div>
      </div>
    </div>
  );
}
